import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Box,
	Text,
	VStack,
	HStack,
	Button,
	Input,
	Avatar,
	Spinner,
	Icon,
	Tag,
	TagLabel,
	FormControl,
	FormLabel,
	FormHelperText,
} from '@chakra-ui/react';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { FiCheckCircle, FiAlertCircle, FiMessageSquare, FiUser } from 'react-icons/fi';
import CountrySelect from './CountrySelect';
import useAppToast from 'components/toast';
import {
	useLeadSuggestionsQuery,
	useLookupConversationQuery,
	useStartConversationMutation,
	useListEligibleChannelsQuery,
} from 'api/metaWhatsappApi';
import SenderSelect from './SenderSelect';
import { explainStartError } from './startErrors';

/**
 * Start a chat with a number that has never written in.
 *
 * ★ THE SERVER DECIDES WHAT THE NUMBER IS.
 * The country select gives a region and the browser shows a preview, but the
 * value that decides which conversation this is comes from the server — the
 * same code that writes it. A browser on different phone metadata previewing
 * one number and creating another is exactly the duplicate-room bug this
 * feature exists to prevent.
 *
 * ★ THE NUMBER FIELD IS FREE TEXT, AND THAT IS DELIBERATE.
 * `03005557777`, `3005557777`, `923005557777`, `+923005557777` and
 * `00923005557777` must all reach the same conversation. A formatted phone
 * widget cannot express that — it owns the value and prepends the selected dial
 * code, so the two spellings that carry their own country code come out wrong.
 * See the note in `CountrySelect.jsx`.
 *
 * ★ AN EXISTING NUMBER OPENS ITS THREAD. Never a second room: the dialog says
 * so before submitting, and the server enforces it regardless.
 *
 * Chakra v1: `isOpen`/`onClose`, `spacing` not `gap`, and `useAppToast` — the
 * Chakra toast crashes this app.
 */

const DEBOUNCE_MS = 250;

/** A value that has stopped changing, so a keystroke is not a request. */
const useDebounced = (value, ms = DEBOUNCE_MS) => {
	const [settled, setSettled] = React.useState(value);
	React.useEffect(() => {
		const t = setTimeout(() => setSettled(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return settled;
};

const when = (date) => {
	if (!date) return '';
	const d = new Date(date);
	const days = Math.round((Date.now() - d.getTime()) / 86400000);
	if (days < 1) return 'today';
	if (days < 30) return `${days}d ago`;
	return d.toLocaleDateString([], { month: 'short', year: 'numeric' });
};

export default function NewChatDialog({ isOpen, onClose, onStarted }) {
	const notify = useAppToast();
	const [raw, setRaw] = React.useState('');
	const [region, setRegion] = React.useState(null);
	const [name, setName] = React.useState('');
	const [leadId, setLeadId] = React.useState(null);
	const [failure, setFailure] = React.useState(null);
	const [senderOpen, setSenderOpen] = React.useState(false);

	const [startConversation, { isLoading }] = useStartConversationMutation();
	const { data: channels = [] } = useListEligibleChannelsQuery(undefined, { skip: !isOpen });

	// The country the tenant messages from is the right default for who they
	// message — nothing stores a region, and the business number is the one
	// international number we always have. Meta always gives it in international
	// form, so it parses without a default of its own.
	//
	// This mirrors `regionFromDisplayNumber` on the server, which is what decides
	// the real answer; agreeing here only means the preview matches the outcome.
	const channelCountry = React.useMemo(() => {
		const display = channels.find((c) => c.isDefault)?.displayPhoneNumber ?? channels[0]?.displayPhoneNumber;
		if (!display) return null;
		const digits = String(display).replace(/\D/g, '');
		return parsePhoneNumberFromString(`+${digits}`)?.country ?? null;
	}, [channels]);

	// Seeded, not defaulted at render: once the agent picks a country it is
	// theirs, and a channel list arriving late must not silently change it.
	React.useEffect(() => {
		if (isOpen && !region && channelCountry) setRegion(channelCountry);
	}, [isOpen, region, channelCountry]);

	const debounced = useDebounced(raw);

	// ★ `currentData`, NEVER `data`.
	// RTK Query's `data` keeps the last successful result when the arguments
	// change or the query is skipped. On a field that is retyped constantly that
	// is not a nicety — it is a lie: measured in the browser, typing a number
	// that has no thread still showed "You already have a chat with this number"
	// left over from the previous number, with the button reading "Open chat".
	// `currentData` is empty until THIS number has an answer.
	const { currentData: suggestions, isFetching } = useLeadSuggestionsQuery(
		{ q: debounced, region },
		{ skip: !isOpen || debounced.replace(/\D/g, '').length < 2 },
	);
	const leads = suggestions?.leads ?? [];
	const phone = suggestions?.phone ?? null;

	// ★ SAY IT BEFORE THEY COMMIT. The server never creates a second room for a
	// number that already has one, but an agent who only learns that afterwards
	// has to work out why the thread they "started" is full of history.
	const { currentData: lookup } = useLookupConversationQuery(
		{ to: debounced, ...(region ? { region } : {}) },
		{ skip: !isOpen || !phone?.valid },
	);
	const existing = (phone?.valid && lookup?.conversation) || null;

	React.useEffect(() => {
		if (!isOpen) {
			setRaw('');
			setRegion(null);
			setName('');
			setLeadId(null);
			setFailure(null);
		}
	}, [isOpen]);

	const pickLead = (lead) => {
		setLeadId(lead._id);
		// The name is a suggestion, not a decision — it stays editable.
		if (!name) setName(lead.leadName ?? '');

		// Verbatim, exactly as the CRM holds it: the field is free text and the
		// server reads every format, so stripping punctuation would only make the
		// agent doubt they picked the right lead.
		const number = lead.leadWhatsappNumber || lead.leadPhoneNumber;
		if (!number) return;
		setRaw(String(number));

		// ★ THE COUNTRY MOVES WITH THE LEAD, or the pick breaks the preview.
		// A lead is very often stored as a bare national number: picking
		// "0300-555-7777" while the business's own country is selected turns a
		// valid preview into "does not look complete", and the agent has to work
		// out for themselves that they must now change a dropdown they never
		// touched. The lead was FOUND by this search, so the country the search
		// resolved to is the right one to carry over; a number that states its own
		// country beats even that.
		const own = parsePhoneNumberFromString(String(number))?.country;
		const inherited = own ?? phone?.country;
		if (inherited) setRegion(inherited);
	};

	const start = async (channelPhoneNumberId) => {
		setFailure(null);
		try {
			const result = await startConversation({
				to: raw,
				...(region ? { region } : {}),
				...(leadId ? { leadId } : {}),
				...(name.trim() ? { customerName: name.trim() } : {}),
				...(channelPhoneNumberId ? { channelPhoneNumberId } : {}),
			}).unwrap();

			setSenderOpen(false);
			onClose();
			onStarted?.(result.conversationId, { existing: result.existing });
			notify.success(
				result.existing ? 'Chat opened' : 'Chat started',
				result.existing
					? 'This number already had a conversation, so it was opened rather than duplicated.'
					: 'WhatsApp needs an approved template to open a new conversation.',
			);
		} catch (err) {
			const payload = err?.data ?? {};
			// Several numbers to send from and none chosen — ask, never guess.
			if (payload.error === 'SENDER_SELECTION_REQUIRED') {
				setSenderOpen(true);
				return;
			}
			setFailure(explainStartError(payload));
		}
	};

	const ready = Boolean(phone?.valid) && !isLoading;

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
				<ModalOverlay />
				<ModalContent bg="bg.surface">
					<ModalHeader fontSize="md" color="text.primary">
						New chat
					</ModalHeader>
					<ModalCloseButton />

					<ModalBody>
						<VStack align="stretch" spacing="4">
							<FormControl>
								<FormLabel fontSize="xs" color="text.secondary" mb="1">
									Phone number
								</FormLabel>
								<HStack spacing="2" align="stretch">
									<CountrySelect
										size="sm"
										flex="0 0 40%"
										value={region}
										preferred={channelCountry}
										onChange={(iso2) => {
											setRegion(iso2);
											setFailure(null);
										}}
									/>
									<Input
										size="sm"
										flex="1"
										bg="bg.input"
										type="tel"
										autoFocus
										placeholder="0300 555 7777"
										aria-label="Customer's number"
										value={raw}
										maxLength={24}
										onChange={(e) => {
											setRaw(e.target.value);
											setFailure(null);
											// A hand-typed number is no longer the lead that was
											// picked; re-picking is one click.
											setLeadId(null);
										}}
									/>
								</HStack>
								<FormHelperText fontSize="0.65rem" color="text.muted" mt="1.5">
									Any format works: 0300 555 7777, 923005557777 or +92 300 555 7777. The country is
									only used when the number does not carry its own.
								</FormHelperText>
							</FormControl>

							{/* What the server will actually store. Shown before submitting so
							    an agent can see a wrong country before creating a thread. */}
							{isFetching && !phone && <Spinner size="xs" />}
							{phone?.valid && (
								<HStack spacing="2" align="start">
									<Icon as={FiCheckCircle} color="success" mt="0.5" />
									<Box>
										<Text fontSize="sm" color="text.primary">
											Will message {phone.formatted}
										</Text>
										<Text fontSize="0.65rem" color="text.muted">
											{phone.country}
											{phone.type && phone.type !== 'MOBILE' ? ' · not a mobile number' : ''}
										</Text>
									</Box>
								</HStack>
							)}

							{/* One number, one chat. Said here so it is not a surprise. */}
							{existing && (
								<HStack
									spacing="2"
									align="start"
									px="3"
									py="2"
									bg="info.subtle"
									borderLeftWidth="3px"
									borderLeftColor="info"
									borderRadius="md"
								>
									<Icon as={FiMessageSquare} color="info" mt="0.5" />
									<Box>
										<Text fontSize="sm" color="text.primary">
											You already have a chat with this number
										</Text>
										<Text fontSize="0.65rem" color="text.muted">
											{existing.channelLabel ? `On ${existing.channelLabel}. ` : ''}
											It will be opened, not duplicated.
											{existing.ownedByOther ? ' Someone else is handling it, so it opens read-only.' : ''}
										</Text>
									</Box>
								</HStack>
							)}
							{/* Why it cannot be used, in the same words the refusal would
							    use — one wording, so the preview and the failure never
							    disagree. Held back until enough has been typed to judge. */}
							{phone && !phone.valid && debounced.replace(/\D/g, '').length >= 6 && (
								<HStack spacing="2" align="start">
									<Icon as={FiAlertCircle} color="warning" mt="0.5" />
									<Text fontSize="sm" color="text.muted">
										{explainStartError({
											error: 'RECIPIENT_INVALID',
											reason: phone.reason,
											assumedRegion: phone.assumedRegion ?? region,
										})}
									</Text>
								</HStack>
							)}

							{/* Leads for this number, newest first — the newest is almost
							    always the one meant. */}
							{leads.length > 0 && (
								<Box>
									<Text fontSize="xs" color="text.muted" mb="1.5">
										{leads.length === 1 ? 'Matching lead' : 'Matching leads'}
									</Text>
									<VStack align="stretch" spacing="1" maxH="180px" overflowY="auto">
										{leads.map((lead, index) => (
											<HStack
												key={lead._id}
												px="2"
												py="1.5"
												borderRadius="md"
												spacing="2"
												cursor="pointer"
												bg={String(leadId) === String(lead._id) ? 'accent.subtle' : 'transparent'}
												_hover={{ bg: 'bg.elevated' }}
												onClick={() => pickLead(lead)}
												role="button"
												aria-label={`Use lead ${lead.leadName}`}
											>
												<Avatar size="xs" name={lead.leadName} />
												<Box flex="1" minW="0">
													<Text fontSize="sm" color="text.primary" noOfLines={1}>
														{lead.leadName}
													</Text>
													<Text fontSize="0.65rem" color="text.muted" noOfLines={1}>
														{lead.leadWhatsappNumber || lead.leadPhoneNumber}
													</Text>
												</Box>
												{index === 0 && leads.length > 1 && (
													<Tag size="sm" colorScheme="green" variant="subtle" borderRadius="full">
														<TagLabel fontSize="0.6rem">Latest</TagLabel>
													</Tag>
												)}
												<Text fontSize="0.65rem" color="text.muted">
													{when(lead.createdDate)}
												</Text>
											</HStack>
										))}
									</VStack>
								</Box>
							)}

							<FormControl>
								<FormLabel fontSize="xs" color="text.secondary" mb="1">
									Name for this chat
								</FormLabel>
								<Input
									size="sm"
									bg="bg.input"
									placeholder="Optional"
									value={name}
									onChange={(e) => setName(e.target.value)}
									maxLength={120}
								/>
								<FormHelperText fontSize="0.65rem" color="text.muted" mt="1">
									Shown in your inbox. Leave it blank to use the customer’s own WhatsApp name once
									they reply.
								</FormHelperText>
							</FormControl>

							{failure && (
								<HStack
									spacing="2"
									align="start"
									px="3"
									py="2"
									bg="bg.subtle"
									borderLeftWidth="3px"
									borderLeftColor="danger"
									borderRadius="md"
								>
									<Icon as={FiAlertCircle} color="danger" mt="0.5" />
									<Text fontSize="sm" color="text.primary">
										{failure}
									</Text>
								</HStack>
							)}

							{leadId && (
								<HStack spacing="1.5">
									<Icon as={FiUser} color="text.muted" boxSize="3" />
									<Text fontSize="0.65rem" color="text.muted">
										This chat will be linked to the selected lead.
									</Text>
								</HStack>
							)}
						</VStack>
					</ModalBody>

					<ModalFooter>
						<Button size="sm" variant="ghost" mr="2" onClick={onClose}>
							Cancel
						</Button>
						<Button
							size="sm"
							colorScheme="green"
							leftIcon={<Icon as={FiMessageSquare} />}
							onClick={() => start()}
							isLoading={isLoading}
							isDisabled={!ready}
						>
							{existing ? 'Open chat' : 'Start chat'}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Several numbers to send from: the server refused to choose, so ask. */}
			<SenderSelect
				isOpen={senderOpen}
				onClose={() => setSenderOpen(false)}
				onSelect={start}
				isSending={isLoading}
			/>
		</>
	);
}
